import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from 'components/common';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { objectSerializer } from 'helpers/utils';

/**
 * Status Kanban for account requests. Each column fetches its own slice of data
 * via `thunk` (one dispatch per column, using the column's status params merged
 * with the shared filter params), so the board reflects the active search /
 * department / site / date filters.
 */
const RequestKanbanBoard = ({ columns, listParams, thunk, detailPath, getTitle, getSubtitle, perColumn }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({});
  const sig = JSON.stringify(listParams || {});

  useEffect(() => {
    let cancelled = false;
    // Drop list-only concerns; keep search/filter params for the board.
    const filters = Object.fromEntries(
      Object.entries(listParams || {}).filter(([k]) => !['page', 'per_page', 'sort'].includes(k))
    );

    columns.forEach(async (col) => {
      setState((prev) => ({ ...prev, [col.key]: { ...(prev[col.key] || {}), loading: true } }));
      try {
        const res = await dispatch(thunk({ ...filters, ...col.params, page: 1, per_page: perColumn }));
        if (cancelled) return;
        const payload = res?.payload || {};
        // Rows come back in JSON:API shape ({ id, attributes }) — flatten them.
        const rows = objectSerializer(payload.rows) || [];
        setState((prev) => ({
          ...prev,
          [col.key]: { rows, total: payload.total_rows ?? rows.length, loading: false },
        }));
      } catch (e) {
        if (!cancelled) setState((prev) => ({ ...prev, [col.key]: { rows: [], total: 0, loading: false } }));
      }
    });

    return () => { cancelled = true; };
    // Refetch only when the composed filter signature changes; columns/thunk are
    // inline literals from the parent and would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);


  return (
    <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-2">
      {columns.map((col) => {
        const colState = state[col.key] || {};
        const rows = colState.rows || [];
        return (
          <div key={col.key} className="tw-flex-shrink-0 tw-w-72 tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-200">
            <div
              className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2.5 tw-border-b tw-border-slate-200 tw-rounded-t-lg"
              style={{ background: `${col.color}12` }}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-w-2.5 tw-h-2.5 tw-rounded-xl" style={{ background: col.color }} />
                <span className="tw-text-sm tw-font-bold tw-text-slate-700">{col.label}</span>
              </div>
              <span className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-px-2 tw-py-0.5">
                {colState.loading ? '…' : (colState.total ?? rows.length)}
              </span>
            </div>

            <div className="tw-p-2 tw-space-y-2 tw-overflow-y-auto" style={{ maxHeight: 'max(240px, calc(100dvh - 360px))' }}>
              {colState.loading ? (
                <div className="tw-flex tw-justify-center tw-py-6"><LoadingSpinner size="sm" /></div>
              ) : rows.length === 0 ? (
                <div className="tw-text-center tw-text-xs tw-text-slate-400 tw-py-6">No requests</div>
              ) : (
                rows.map((row) => (
                  <Link
                    key={row.id}
                    to={`${detailPath}/${row.id}`}
                    target="_blank"
                    className="tw-block tw-bg-white tw-rounded tw-border tw-border-slate-200 tw-p-3 tw-no-underline hover:tw-no-underline hover:tw-border-primary-300 hover:tw-shadow-sm tw-transition-all"
                  >
                    <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-mb-0.5 tw-truncate">{getTitle(row)}</p>
                    {getSubtitle(row) && <p className="tw-text-xs tw-text-slate-500 tw-mb-1.5 tw-truncate">{getSubtitle(row)}</p>}
                    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-slate-400 tw-gap-2">
                      <span className="tw-truncate">{row.site?.name || '—'}</span>
                      <span className="tw-flex-shrink-0">{row.created_date || ''}</span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

RequestKanbanBoard.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    params: PropTypes.object,
  })).isRequired,
  listParams: PropTypes.object,
  thunk: PropTypes.func.isRequired,
  detailPath: PropTypes.string.isRequired,
  getTitle: PropTypes.func.isRequired,
  getSubtitle: PropTypes.func,
  perColumn: PropTypes.number,
};

RequestKanbanBoard.defaultProps = {
  listParams: {},
  getSubtitle: () => null,
  perColumn: 100,
};

export default RequestKanbanBoard;
